import {makeAutoObservable, reaction} from "mobx";
import {KeyValue} from "../../../observables/keyValue";
import {NumberObservable} from "../../../observables/numberObservable";
import {
  maxWeeksBeforeEvent,
  maxDaysBeforeEvent,
  maxHoursBeforeEvent
} from "../../../../shared/constants";
import {ObservableList} from "../../../observables/observableList";
import {parseDuration} from "../../../../utils/dateUtils";

const capacityMax = 500;

export class EventBookingDeadlineDetails {
  hasBookingDeadline: boolean;
  weeks: ObservableList<KeyValue>;
  days: ObservableList<KeyValue>;
  hours: ObservableList<KeyValue>;
  capacity: NumberObservable;
  useValidation: boolean;
  isDirty: boolean;
  datesAreDirty: boolean;
  initialBookingDeadline: boolean;

  constructor(
    bookingDeadline?: string,
    capacity?: number,
    useValidation?: boolean
  ) {
    makeAutoObservable(this, {
      generateSelectItems: false
    });

    const deadline = parseDuration(bookingDeadline, true);
    this.hasBookingDeadline =
      bookingDeadline !== null && bookingDeadline !== undefined;
    this.initialBookingDeadline = this.hasBookingDeadline;
    this.weeks = new ObservableList<KeyValue>(
      this.generateSelectItems(maxWeeksBeforeEvent, "week"),
      deadline.weeks?.toString()
    );

    this.days = new ObservableList<KeyValue>(
      this.generateSelectItems(maxDaysBeforeEvent, "day"),
      deadline.days?.toString()
    );
    this.hours = new ObservableList<KeyValue>(
      this.generateSelectItems(maxHoursBeforeEvent, "hour"),
      deadline.hours?.toString()
    );

    this.capacity = new NumberObservable(
      capacity,
      0,
      capacityMax,
      0,
      (toValidate) => {
        return (
          toValidate !== undefined &&
          toValidate >= 0 &&
          toValidate <= capacityMax
        );
      }
    );
    this.useValidation = useValidation ?? false;
    this.isDirty = false;
    this.datesAreDirty = false;
    reaction(
      () => this.weeks.selectedItem,
      () => {
        this.setDatesAreDirty(true);
      }
    );
    reaction(
      () => this.days.selectedItem,
      () => {
        this.setDatesAreDirty(true);
      }
    );
    reaction(
      () => this.hours.selectedItem,
      () => {
        this.setDatesAreDirty(true);
      }
    );
    reaction(
      () => this.hasBookingDeadline,
      () => {
        this.setIsDirty(true);
      }
    );
    reaction(
      () => this.capacity.number,
      () => {
        this.setIsDirty(true);
      }
    );
  }

  generateSelectItems = (noOfItems: number, unit: string) => {
    let items = [];
    for (let i = 0; i < noOfItems + 1; i++) {
      let unitText = i !== 1 ? unit + "s" : unit;
      items.push(new KeyValue(i.toString(), i.toString() + " " + unitText));
    }
    return items;
  };

  setHasBookingDeadline = (value: boolean) => {
    this.hasBookingDeadline = value;
  };

  setIsDirty = (value: boolean) => {
    this.isDirty = value;
  };

  setDatesAreDirty = (value: boolean) => {
    this.datesAreDirty = value;
  };

  hasBookingDeadlineChange(): boolean {
    return this.weeks.hasChange || this.days.hasChange || this.hours.hasChange;
  }

  get isValid(): boolean {
    return !this.useValidation || this.validate();
  }

  validate = (): boolean => {
    return this.isDeadlineValid && this.isCapacityValid;
  };

  get isCapacityValid(): boolean {
    return (
      !this.hasBookingDeadline ||
      (this.capacity.isValid && this.capacity.number !== undefined)
    );
  }

  get isDeadlineValid(): boolean {
    return (
      !this.hasBookingDeadline ||
      !this.datesAreDirty ||
      Number(this.weeks.selectedItem?.value) > 0 ||
      Number(this.days.selectedItem?.value) > 0 ||
      Number(this.hours.selectedItem?.value) > 0
    );
  }
}
